import { type ReactNode, useEffect, useMemo } from 'react'
import { createLayeredContext } from '@/contexts/layered-context'
import { normalizeCssNumber } from '@/utils/browser-util'

const {
  Provider: ScrollPaddingTopContextProvider,
  useProvide: useProvideScrollPaddingTopValue,
  useValue: useScrollPaddingTopValue,
} = createLayeredContext<string | number | null>(null, {
  excludeNulls: true,
})

/**
 * Provides a scroll-padding-top to use for the document.
 *
 * @param value
 * @param priority
 */
export function useProvideScrollPaddingTop(
  value: string | number | null,
  priority?: number,
) {
  return useProvideScrollPaddingTopValue(value, priority)
}

/**
 * Applies the provided scroll-padding-top to the document.
 */
function useScrollPaddingTop() {
  const offset = useScrollPaddingTopValue()
  const scrollPaddingTop = useMemo(() => normalizeCssNumber(offset), [offset])

  useEffect(() => {
    if (scrollPaddingTop === null) {
      return
    }

    document.documentElement.style.scrollPaddingTop = scrollPaddingTop

    return () => {
      document.documentElement.style.removeProperty('scroll-padding-top')
    }
  }, [scrollPaddingTop])
}

const {
  Provider: ScrollPaddingBottomContextProvider,
  useProvide: useProvideScrollPaddingBottomValue,
  useValue: useScrollPaddingBottomValue,
} = createLayeredContext<string | number | null>(null, {
  excludeNulls: true,
})

/**
 * Provides a scroll-padding-bottom to use for the document.
 *
 * @param value
 * @param priority
 */
export function useProvideScrollPaddingBottom(
  value: string | number | null,
  priority?: number,
) {
  return useProvideScrollPaddingBottomValue(value, priority)
}

/**
 * Applies the provided scroll-padding-bottom to the document.
 */
function useScrollPaddingBottom() {
  const offset = useScrollPaddingBottomValue()
  const scrollPaddingBottom = useMemo(
    () => normalizeCssNumber(offset),
    [offset],
  )

  useEffect(() => {
    if (scrollPaddingBottom === null) {
      return
    }

    document.documentElement.style.scrollPaddingBottom = scrollPaddingBottom

    return () => {
      document.documentElement.style.removeProperty('scroll-padding-bottom')
    }
  }, [scrollPaddingBottom])
}

/**
 * Scroll padding providers.
 *
 * @param children
 * @constructor
 */
export function ScrollPaddingProvider({ children }: { children: ReactNode }) {
  return (
    <ScrollPaddingTopContextProvider>
      <ScrollPaddingBottomContextProvider>
        <ScrollPaddingConsumerInner>{children}</ScrollPaddingConsumerInner>
      </ScrollPaddingBottomContextProvider>
    </ScrollPaddingTopContextProvider>
  )
}

/**
 * Inner component that consumes the scroll padding.
 * @param children
 * @constructor
 */
function ScrollPaddingConsumerInner({ children }: { children: ReactNode }) {
  useScrollPaddingTop()
  useScrollPaddingBottom()

  return children
}
